import type { PageId } from "./types";

/**
 * État poussé dans `history.state`. `step`/`ineligible` sont propres au parcours du
 * formulaire (voir pages/formulaire.ts) — le router n'en connaît pas la sémantique, il se
 * contente de les transporter pour que `onPopState` puisse les restituer tels quels.
 */
export interface NavState {
  page: PageId;
  step?: number;
  ineligible?: string;
}

type PageChangeListener = (page: PageId) => void;
type PopStateListener = (state: NavState) => void;

const listeners: PageChangeListener[] = [];
const popStateListeners: PopStateListener[] = [];

const PAGE_TITLES: Record<PageId, string> = {
  landing: "Start AI by Astek — Financement IA pour PME wallonnes",
  offre: "L'offre Start IA — Start AI by Astek",
  formulaire: "Vérifiez votre éligibilité — Start AI by Astek",
};

/** Id DOM de la section correspondant à une page. Pure — pas d'accès au DOM. */
export function resolvePageElementId(pageId: PageId): string {
  return `page-${pageId}`;
}

/** Titre d'onglet à afficher pour une page. Pure — pas d'accès au DOM. */
export function resolvePageTitle(pageId: PageId): string {
  return PAGE_TITLES[pageId];
}

/** Enregistre un listener appelé après chaque changement de page déclenché par un clic (`goTo`). */
export function onPageChange(listener: PageChangeListener): void {
  listeners.push(listener);
}

/**
 * Enregistre un listener appelé uniquement lors d'un retour/avance via le bouton du
 * navigateur (jamais lors d'un clic sur un lien du site — voir `goTo` vs `popstate`).
 * Sert à formulaire.ts pour restituer l'étape ou l'écran d'inéligibilité exacts au lieu de
 * repartir de zéro comme le ferait une navigation normale vers le formulaire.
 */
export function onPopState(listener: PopStateListener): void {
  popStateListeners.push(listener);
}

/**
 * Déplace le focus clavier vers le contenu de la page qui vient de s'afficher (le `<h1>`
 * s'il existe, sinon le conteneur lui-même) : annonce la navigation aux lecteurs d'écran
 * et repositionne les utilisateurs clavier, comme le ferait un vrai changement de route.
 */
function focusPageContent(target: HTMLElement): void {
  const heading = target.querySelector<HTMLElement>("h1") ?? target;
  if (!heading.hasAttribute("tabindex")) {
    heading.setAttribute("tabindex", "-1");
  }
  heading.focus({ preventScroll: true });
}

/** Pointe le lien d'évitement ("Aller au contenu principal") vers le `<main>` de la page active. */
function updateSkipLink(pageId: PageId): void {
  const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
  if (skipLink) {
    skipLink.href = `#main-content-${pageId}`;
  }
}

/** Id de la page actuellement affichée, déduit du DOM plutôt que suivi séparément en mémoire. */
function currentPageId(): PageId | undefined {
  const activeId = document.querySelector<HTMLElement>(".page.active")?.id;
  return activeId?.replace("page-", "") as PageId | undefined;
}

/**
 * Bascule l'affichage vers `pageId` — effet purement visuel, ne touche jamais à l'historique
 * du navigateur (voir `goTo` pour la navigation normale, et le handler `popstate` dans
 * `initRouter` pour le retour/avance navigateur, qui appellent tous deux cette fonction).
 */
function renderPage(pageId: PageId): void {
  document.querySelectorAll<HTMLElement>(".page").forEach((el) => el.classList.remove("active"));
  const target = document.getElementById(resolvePageElementId(pageId));
  if (!target) {
    console.warn(`[router] Unknown page: ${pageId}`);
    return;
  }
  target.classList.add("active");
  document.title = resolvePageTitle(pageId);
  updateSkipLink(pageId);
  window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  focusPageContent(target);
}

/**
 * Navigue vers `pageId` suite à une interaction utilisateur (clic) : affiche la page, empile
 * une entrée d'historique — le bouton "Précédent" du navigateur ramène alors à la page
 * précédente au lieu de quitter le site, voir `initRouter` — puis notifie les listeners
 * "normaux" (ex : réinitialiser le formulaire à chaque arrivée sur cette page).
 */
export function goTo(pageId: PageId): void {
  renderPage(pageId);
  const state: NavState = { page: pageId };
  history.pushState(state, "", `#${pageId}`);
  listeners.forEach((listener) => listener(pageId));
}

/**
 * Empile (ou remplace) une entrée d'historique pour un état interne à la page courante — ex :
 * une étape du formulaire de qualification — sans rejouer `goTo` ni ses listeners de page.
 * Seul `onPopState` est notifié quand le navigateur y revient (bouton "Précédent"/"Suivant").
 */
export function pushNavState(state: NavState, options: { replace?: boolean } = {}): void {
  const hash = `#${state.page}`;
  if (options.replace) {
    history.replaceState(state, "", hash);
  } else {
    history.pushState(state, "", hash);
  }
}

/** Câble tous les éléments `[data-goto]` du document pour déclencher `goTo` au clic, et active le support du bouton "Précédent"/"Suivant" du navigateur. */
export function initRouter(): void {
  document.querySelectorAll<HTMLElement>("[data-goto]").forEach((el) => {
    el.addEventListener("click", (event) => {
      // Les liens <a data-goto> portent un href="#" pour rester focusables/accessibles au
      // clavier ; on empêche le saut d'ancre par défaut puisque la navigation est gérée en JS.
      event.preventDefault();
      const page = el.dataset.goto as PageId | undefined;
      if (page) goTo(page);
    });
  });

  // Établit l'entrée d'historique initiale (remplace plutôt que d'empiler) : sans elle, le
  // tout premier clic sur "Précédent" quitterait le site au lieu de rester dessus.
  const initialState: NavState = { page: currentPageId() ?? "landing" };
  history.replaceState(initialState, "", location.hash || "#landing");

  window.addEventListener("popstate", (event) => {
    const state = (event.state ?? { page: "landing" }) as NavState;
    if (state.page !== currentPageId()) {
      renderPage(state.page);
    }
    popStateListeners.forEach((listener) => listener(state));
  });
}
